import React, { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import Card from '../Card/Card';
import ChevronLink from '../ChevronLink/ChevronLink';
import './ContentSection.css';

export type ContentSectionPropsType = {
    title?: string;
    level?: 1 | 2 | 3 | 4 | 5 | 6;
    subtitle?: string;
    children?: React.ReactNode;
    className?: string;
    contentClassName?: string;
    titleElement?: React.ReactNode | React.ReactElement;
    titleElementCondition?: boolean;
    showMoreChevronLinkProps?: {
        text: string;
        link: string;
    };
    showMoreContentProps?: {
        onClick: () => void;
        condition: boolean;
    };
};

function slugify(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

const ContentSection = ({
    title,
    level = 3,
    subtitle,
    children,
    className,
    contentClassName,
    titleElement,
    titleElementCondition = true,
    showMoreChevronLinkProps,
    showMoreContentProps
}: ContentSectionPropsType) => {
    const { t } = useTranslation(['datasets', 'translation']);
    const [isHovered, setIsHovered] = useState(false);
    const toast = useRef<Toast>(null);
    const sectionId = title ? slugify(title) : undefined;

    const handleCopyLink = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!sectionId) return;
        const baseUrl = window.location.href.split('#')[0];
        const linkUrl = `${baseUrl}#${sectionId}`;
        navigator.clipboard.writeText(linkUrl).then(() => {
            toast.current?.show({
                severity: 'success',
                summary: t('ContentSection.saveSuccess'),
                detail: t('ContentSection.copyLinkSuccess'),
                life: 3000
            });
        }).catch(() => {
            toast.current?.show({
                severity: 'error',
                summary: t('ContentSection.saveError'),
                detail: t('ContentSection.copyLinkError'),
                life: 3000
            });
        });
    };

    const HeadingTag = title ? (`h${level}` as keyof React.JSX.IntrinsicElements) : null;

    return (
        <>
            <Card elevation={1} className={`content-section no-hover ${className || ''}`}>
                {title && HeadingTag && (
                    <div className="title-container">
                        <div
                            className="content-section-title-wrap"
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            <HeadingTag
                                id={sectionId}
                                className="content-section-heading"
                                onClick={(e) => {
                                    if (typeof window !== 'undefined' && (window.ontouchstart != null || window.innerWidth <= 1024)) {
                                        handleCopyLink(e);
                                    }
                                }}
                            >
                                {title}
                                {sectionId && (
                                    <>
                                        <i
                                            className="pi pi-link content-section-anchor-desktop"
                                            onClick={handleCopyLink}
                                            aria-label={t('ContentSection.copySelectionLink')}
                                        />
                                        <i
                                            className="pi pi-link content-section-anchor-touch"
                                            onClick={handleCopyLink}
                                            aria-label={t('ContentSection.copySelectionLink')}
                                        />
                                    </>
                                )}
                            </HeadingTag>
                        </div>
                        {titleElement && titleElementCondition && <div className='title-element'>{titleElement}</div>}
                    </div>
                )}
                {subtitle && <p className="content-section-subtitle">{subtitle}</p>}
                <div className={`content-section-content ${contentClassName || ''} ${!title ? 'no-title' : ''}`}>
                    {children}
                </div>
                {(showMoreContentProps?.condition || showMoreChevronLinkProps) && (
                    <div className={`section-footer${showMoreContentProps?.condition ? ' show-more-visible' : ''}`}>
                        {showMoreContentProps?.condition && (
                            <ChevronLink
                                chevronDirection='left-down'
                                linkDisabled
                                text={t('Section.showMore', {ns: 'translation'})}
                                onClick={showMoreContentProps.onClick}
                            />
                        )}
                        {showMoreChevronLinkProps && (
                            <ChevronLink
                                text={showMoreChevronLinkProps.text}
                                linkUrl={showMoreChevronLinkProps.link}
                                chevronDirection='right'
                                className='show-more-link'
                            />
                        )}
                    </div>
                )}
            </Card>
            <Toast ref={toast} position="center" />
        </>
    );
};

export default ContentSection;
